// ─────────────────────────────────────────────
// KLOTHIQ — Report Detail Page (Reusable Shell)
// FEAT-REPORTS-001 §3 FR-RPT-005
// ─────────────────────────────────────────────
// One shell serves all 6 reports. Route: /reports/:category/:type
// 4-tier layout: Toolbar → Insight Strip → Chart → Data Table

import { Input } from '@/components/ui/FormControls';
import DateRangeFields from '@/components/common/DateRangeFields';
import { useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Link from '@/components/common/AccessLink';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
  PieChart,
  Pie,
} from 'recharts';
import {
  ArrowLeft,
  TrendingUp,
  ShoppingBag,
  Warehouse,
  Clock,
  ClipboardList,
  RotateCcw,
  ShieldAlert,
  CalendarX,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Search,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import PageHeader from '@/components/common/PageHeader';
import EmptyState from '@/components/common/EmptyState';
import ErrorState from '@/components/common/ErrorState';
import ExportActions from '@/components/common/ExportActions';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/utils/exportUtils';
import type { DateRangeFilter, ReportInsight } from './reportTypes';
import {
  useSalesSummaryReport,
  useSalesByProductReport,
  useStockValuationReport,
  useReceivablesAgingReport,
  usePurchasesReport,
  useReturnsSummaryReport,
} from './useReportData';

// ── Report Registry ──────────────────────────────────────────

interface ReportMeta {
  title: string;
  description: string;
  icon: React.ReactNode;
  roles?: ('owner' | 'admin' | 'staff' | 'viewer')[];
  needsDateRange: boolean;
  statusFilters?: { value: string; label: string }[];
  dimensionFilters?: { value: string; label: string }[];
}

const REPORT_META: Record<string, ReportMeta> = {
  'sales/summary': {
    title: 'Sales Summary',
    description:
      'Original invoice values exclude drafts, cancelled and fully returned orders. Partial returns need balance review. Collected columns are lifetime ledger values for these sale dates; the receipt insight uses payment dates.',
    icon: <TrendingUp className="h-5 w-5" />,
    needsDateRange: true,
  },
  'sales/by-product': {
    title: 'Sales by Product / Team / Category',
    description:
      'Original product-line invoice values, excluding delivery charges, drafts, cancelled and fully returned orders. Partial-return lines remain original values.',
    icon: <ShoppingBag className="h-5 w-5" />,
    needsDateRange: true,
    dimensionFilters: [
      { value: 'product', label: 'By Product' },
      { value: 'team', label: 'By Team' },
      { value: 'category', label: 'By Category' },
    ],
  },
  'inventory/valuation': {
    title: 'Stock Valuation & Low Stock',
    description: 'Inventory capital tie-up and low stock report',
    icon: <Warehouse className="h-5 w-5" />,
    needsDateRange: false,
    statusFilters: [
      { value: 'all', label: 'All Stock' },
      { value: 'low_stock', label: 'Low Stock Only' },
      { value: 'out_of_stock', label: 'Out of Stock Only' },
    ],
  },
  'financial/receivables-aging': {
    title: 'Receivables Aging',
    description: 'Overdue customer balances by aging bucket',
    icon: <Clock className="h-5 w-5" />,
    roles: ['owner', 'admin'],
    needsDateRange: false,
  },
  'purchases/summary': {
    title: 'Purchases & Payables',
    description:
      'Active purchases by order date, with lifetime payments and current balances. Draft, cancelled and returned orders are excluded.',
    icon: <ClipboardList className="h-5 w-5" />,
    roles: ['owner', 'admin'],
    needsDateRange: true,
    statusFilters: [
      { value: 'all', label: 'Active Purchases' },
      { value: 'unpaid', label: 'Unpaid / Partial Only' },
    ],
  },
  'returns/summary': {
    title: 'Returns Summary',
    description: 'Return lifecycle, reasons, and refund tracking',
    icon: <RotateCcw className="h-5 w-5" />,
    needsDateRange: true,
  },
};

// ── Chart Colors ──────────────────────────────────────────────
const CHART_COLORS = [
  'hsl(187, 71%, 50%)', // cyan
  'hsl(160, 60%, 45%)', // emerald
  'hsl(250, 60%, 55%)', // purple
  'hsl(35, 92%, 55%)', // amber
  'hsl(345, 65%, 55%)', // rose
  'hsl(210, 65%, 55%)', // blue
  'hsl(120, 50%, 45%)', // green
  'hsl(280, 55%, 50%)', // violet
];

// ── Helpers ──────────────────────────────────────────────────

function getDefaultDateRange(): DateRangeFilter {
  const now = new Date();
  const to = now.toISOString().slice(0, 10);
  const from6m = new Date(now);
  from6m.setMonth(from6m.getMonth() - 6);
  const from = from6m.toISOString().slice(0, 10);
  return { from, to };
}

function parseSearchParamDates(searchParams: URLSearchParams): DateRangeFilter {
  const range = searchParams.get('range');
  const now = new Date();
  const to = now.toISOString().slice(0, 10);

  if (range === '7d') {
    const d = new Date(now);
    d.setDate(d.getDate() - 7);
    return { from: d.toISOString().slice(0, 10), to };
  }
  if (range === '30d') {
    const d = new Date(now);
    d.setDate(d.getDate() - 30);
    return { from: d.toISOString().slice(0, 10), to };
  }
  if (range === '1y') {
    const d = new Date(now);
    d.setFullYear(d.getFullYear() - 1);
    return { from: d.toISOString().slice(0, 10), to };
  }

  const fromParam = searchParams.get('from');
  const toParam = searchParams.get('to');
  if (fromParam && toParam) return { from: fromParam, to: toParam };

  return getDefaultDateRange(); // default 6m
}

const PAGE_SIZE = 20;

// ── Main Component ───────────────────────────────────────────

export default function ReportDetailPage() {
  const { category, type } = useParams<{ category: string; type: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { profile } = useAuth();
  const userRole = (profile?.role as 'owner' | 'admin' | 'staff' | 'viewer') ?? 'staff';

  const reportKey = `${category}/${type}`;
  const meta = REPORT_META[reportKey];

  // State
  const [dateRange, setDateRange] = useState<DateRangeFilter>(() =>
    parseSearchParamDates(searchParams),
  );
  const [statusFilter, setStatusFilter] = useState('all');
  const [dimensionFilter, setDimensionFilter] = useState(
    meta?.dimensionFilters?.[0]?.value ?? 'product',
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);

  // ── Role Guard ─────────────────────────────────────────────
  if (!meta) {
    return (
      <div className="space-y-6">
        <PageHeader title="Report Not Found" backTo="/reports" />
        <EmptyState
          icon={<CalendarX className="h-7 w-7" />}
          title="Report not found"
          description="The requested report does not exist."
          action={
            <Link to="/reports" className="text-sm font-medium text-primary hover:underline">
              ← Back to Reports Hub
            </Link>
          }
        />
      </div>
    );
  }

  if (meta.roles && !meta.roles.includes(userRole)) {
    return (
      <div className="space-y-6">
        <PageHeader title={meta.title} backTo="/reports" />
        <div className="flex flex-col items-center justify-center rounded-xl border border-destructive/20 bg-destructive/5 px-6 py-16 text-center">
          <ShieldAlert className="mb-4 h-12 w-12 text-destructive/60" />
          <h3 className="text-base font-bold text-foreground">Access Restricted</h3>
          <p className="mt-2 max-w-sm text-sm text-muted-foreground">
            This report is only available to Admin and Owner roles. Contact your administrator for
            access.
          </p>
          <Link
            to="/reports"
            className="mt-5 inline-flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground shadow-sm hover:bg-accent"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Reports
          </Link>
        </div>
      </div>
    );
  }

  // ── Data Hooks (conditional call based on report) ──────────
  return (
    <ReportDetailContent
      reportKey={reportKey}
      meta={meta}
      dateRange={dateRange}
      setDateRange={(dr) => {
        setDateRange(dr);
        setSearchParams({ from: dr.from, to: dr.to });
        setCurrentPage(1);
      }}
      statusFilter={statusFilter}
      setStatusFilter={(v) => {
        setStatusFilter(v);
        setCurrentPage(1);
      }}
      dimensionFilter={dimensionFilter}
      setDimensionFilter={(v) => {
        setDimensionFilter(v);
        setCurrentPage(1);
      }}
      searchQuery={searchQuery}
      setSearchQuery={(v) => {
        setSearchQuery(v);
        setCurrentPage(1);
      }}
      sortKey={sortKey}
      sortDir={sortDir}
      onSort={(key) => {
        if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        else {
          setSortKey(key);
          setSortDir('desc');
        }
      }}
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
    />
  );
}

// ── Inner content component (so hooks are always called) ─────

interface ContentProps {
  reportKey: string;
  meta: ReportMeta;
  dateRange: DateRangeFilter;
  setDateRange: (dr: DateRangeFilter) => void;
  statusFilter: string;
  setStatusFilter: (v: string) => void;
  dimensionFilter: string;
  setDimensionFilter: (v: string) => void;
  searchQuery: string;
  setSearchQuery: (v: string) => void;
  sortKey: string | null;
  sortDir: 'asc' | 'desc';
  onSort: (key: string) => void;
  currentPage: number;
  setCurrentPage: (p: number) => void;
}

function ReportDetailContent({
  reportKey,
  meta,
  dateRange,
  setDateRange,
  statusFilter,
  setStatusFilter,
  dimensionFilter,
  setDimensionFilter,
  searchQuery,
  setSearchQuery,
  sortKey,
  sortDir,
  onSort,
  currentPage,
  setCurrentPage,
}: ContentProps) {
  // Call ALL hooks unconditionally to comply with Rules of Hooks
  const salesSummary = useSalesSummaryReport(dateRange);
  const salesByProduct = useSalesByProductReport(dateRange, dimensionFilter);
  const stockValuation = useStockValuationReport(statusFilter);
  const receivablesAging = useReceivablesAgingReport();
  const purchasesReport = usePurchasesReport(dateRange, statusFilter);
  const returnsSummary = useReturnsSummaryReport(dateRange);

  // Select the active report data
  const activeQuery = useMemo(() => {
    switch (reportKey) {
      case 'sales/summary':
        return salesSummary;
      case 'sales/by-product':
        return salesByProduct;
      case 'inventory/valuation':
        return stockValuation;
      case 'financial/receivables-aging':
        return receivablesAging;
      case 'purchases/summary':
        return purchasesReport;
      case 'returns/summary':
        return returnsSummary;
      default:
        return salesSummary;
    }
  }, [
    reportKey,
    salesSummary,
    salesByProduct,
    stockValuation,
    receivablesAging,
    purchasesReport,
    returnsSummary,
  ]);

  const { data, isLoading, error, refetch } = activeQuery;
  const chartData = data?.chartData ?? [];
  const rawTableData = (data?.tableData ?? []) as Record<string, unknown>[];
  const insights: ReportInsight[] = data?.insights ?? [];

  // Search & Sort
  const filteredData = useMemo(() => {
    let result = rawTableData;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((row) =>
        Object.values(row).some((v) =>
          String(v ?? '')
            .toLowerCase()
            .includes(q),
        ),
      );
    }
    if (sortKey) {
      result = [...result].sort((a, b) => {
        const aVal = a[sortKey] ?? '';
        const bVal = b[sortKey] ?? '';
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
        }
        return sortDir === 'asc'
          ? String(aVal).localeCompare(String(bVal))
          : String(bVal).localeCompare(String(aVal));
      });
    }
    return result;
  }, [rawTableData, searchQuery, sortKey, sortDir]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredData.length / PAGE_SIZE));
  const pageData = filteredData.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Column keys from table data
  const columnKeys = useMemo(() => {
    if (rawTableData.length === 0) return [];
    return Object.keys(rawTableData[0]!);
  }, [rawTableData]);

  // Column label formatter
  function colLabel(key: string): string {
    if (reportKey === 'sales/by-product' && key === 'name')
      return dimensionFilter === 'category'
        ? 'Category'
        : dimensionFilter === 'team'
          ? 'Team'
          : 'Product';
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/^\w/, (c) => c.toUpperCase())
      .trim();
  }

  // Cell formatter
  function formatCell(key: string, value: unknown): string {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'number') {
      if (key.toLowerCase().includes('percent') || key.toLowerCase().includes('margin')) {
        return `${value >= 0 ? '' : '-'}${Math.abs(value).toFixed(1)}%`;
      }
      if (
        key.toLowerCase().includes('revenue') ||
        key.toLowerCase().includes('cost') ||
        key.toLowerCase().includes('amount') ||
        key.toLowerCase().includes('profit') ||
        key.toLowerCase().includes('balance') ||
        key.toLowerCase().includes('price') ||
        key.toLowerCase().includes('value') ||
        key.toLowerCase().includes('current') ||
        key.toLowerCase().includes('thirty') ||
        key.toLowerCase().includes('sixty') ||
        key.toLowerCase().includes('ninety')
      ) {
        return formatCurrency(value);
      }
      return value.toLocaleString('en-IN');
    }
    return String(value);
  }

  // Export configs
  const safeBaseName = meta.title.replace(/[/\\?*:[\]\s]+/g, '_');
  const safeSheet = meta.title
    .replace(/[/\\?*:[\]]/g, ' - ')
    .replace(/\s+/g, ' ')
    .slice(0, 31)
    .trim();

  const getExcelOptions = () => ({
    fileName: `${safeBaseName}_Report`,
    sheetName: safeSheet || 'Report',
    columns: columnKeys.map((key) => ({
      header: colLabel(key),
      key,
      format:
        key.toLowerCase().includes('amount') ||
        key.toLowerCase().includes('revenue') ||
        key.toLowerCase().includes('cost') ||
        key.toLowerCase().includes('price') ||
        key.toLowerCase().includes('profit') ||
        key.toLowerCase().includes('balance') ||
        key.toLowerCase().includes('value')
          ? ('currency' as const)
          : ('text' as const),
    })),
    data: filteredData,
    filtersSummary: meta.needsDateRange ? `${dateRange.from} to ${dateRange.to}` : undefined,
  });

  const getPdfOptions = () => ({
    fileName: `${safeBaseName}_Report`,
    title: meta.title,
    subtitle: meta.needsDateRange ? `${dateRange.from} to ${dateRange.to}` : 'Current Snapshot',
    columns: columnKeys.map((key) => ({
      header: colLabel(key),
      key,
      format:
        key.toLowerCase().includes('amount') ||
        key.toLowerCase().includes('revenue') ||
        key.toLowerCase().includes('cost') ||
        key.toLowerCase().includes('price') ||
        key.toLowerCase().includes('profit') ||
        key.toLowerCase().includes('balance') ||
        key.toLowerCase().includes('value')
          ? ('currency' as const)
          : ('text' as const),
    })),
    rows: filteredData,
    summaryLines: insights.map((i) => ({ label: i.label, value: i.value })),
    orientation: 'landscape' as const,
  });

  return (
    <div className="space-y-5">
      {/* ── Tier 1: Header & Toolbar ── */}
      <div className="flex flex-col gap-4">
        <PageHeader
          title={meta.title}
          description={meta.description}
          backTo="/reports"
          actions={
            <ExportActions
              excelOptions={getExcelOptions}
              pdfOptions={getPdfOptions}
              onPrint={() => window.print()}
              disabled={isLoading || filteredData.length === 0}
            />
          }
        />

        {/* Toolbar Row */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Date Range */}
          {meta.needsDateRange && (
            <DateRangeFields
              from={dateRange.from}
              to={dateRange.to}
              onFromChange={(value) => setDateRange({ ...dateRange, from: value ?? '' })}
              onToChange={(value) => setDateRange({ ...dateRange, to: value ?? '' })}
            />
          )}

          {/* Status Filters */}
          {meta.statusFilters && (
            <div className="obsidian-surface flex items-center gap-1 rounded-lg border border-border/80 bg-card px-1.5 py-1 text-xs shadow-xs">
              {meta.statusFilters.map((sf) => (
                <button
                  key={sf.value}
                  onClick={() => setStatusFilter(sf.value)}
                  className={cn(
                    'rounded-md px-2.5 py-1 text-[11px] font-semibold transition-all',
                    statusFilter === sf.value
                      ? 'bg-primary text-primary-foreground shadow-xs'
                      : 'hover:bg-muted/60 text-muted-foreground',
                  )}
                >
                  {sf.label}
                </button>
              ))}
            </div>
          )}

          {/* Dimension Filters */}
          {meta.dimensionFilters && (
            <div className="obsidian-surface flex items-center gap-1 rounded-lg border border-border/80 bg-card px-1.5 py-1 text-xs shadow-xs">
              {meta.dimensionFilters.map((df) => (
                <button
                  key={df.value}
                  onClick={() => setDimensionFilter(df.value)}
                  className={cn(
                    'rounded-md px-2.5 py-1 text-[11px] font-semibold transition-all',
                    dimensionFilter === df.value
                      ? 'bg-primary text-primary-foreground shadow-xs'
                      : 'hover:bg-muted/60 text-muted-foreground',
                  )}
                >
                  {df.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Error State ── */}
      {error && (
        <ErrorState
          title="Failed to load report"
          message={error instanceof Error ? error.message : 'An unexpected error occurred'}
          onRetry={() => refetch()}
        />
      )}

      {/* ── Loading State ── */}
      {isLoading && (
        <div className="space-y-4">
          {/* Insight skeleton */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="glass-card animate-pulse p-4">
                <div className="h-3 w-16 rounded bg-muted/40 mb-2" />
                <div className="h-5 w-24 rounded bg-muted/60" />
              </div>
            ))}
          </div>
          {/* Chart skeleton */}
          <div className="glass-card animate-pulse p-6">
            <div className="h-[280px] rounded-lg bg-muted/20" />
          </div>
          {/* Table skeleton */}
          <div className="glass-card animate-pulse p-4">
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-8 rounded bg-muted/20" />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Loaded Content ── */}
      {!isLoading && !error && (
        <>
          {/* ── Tier 2: Insight Strip ── */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {insights.map((insight, i) => (
              <div
                key={i}
                className={cn(
                  'glass-card p-4 transition-all',
                  insight.alertType === 'emerald' && 'border-l-2 border-l-emerald-500',
                  insight.alertType === 'amber' && 'border-l-2 border-l-amber-500',
                  insight.alertType === 'red' && 'border-l-2 border-l-rose-500',
                  insight.alertType === 'neutral' && 'border-l-2 border-l-primary/40',
                )}
              >
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {insight.label}
                </p>
                <p className="mt-1 text-lg font-bold text-foreground">{insight.value}</p>
                {insight.trendLabel && (
                  <p
                    className={cn(
                      'mt-0.5 text-[10px] font-bold',
                      insight.isPositive ? 'text-emerald-500' : 'text-rose-500',
                    )}
                  >
                    {insight.trendLabel}
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* ── Tier 3: Chart ── */}
          {chartData.length > 0 ? (
            <div className="glass-card p-4 sm:p-6">
              <div className="h-[260px] sm:h-[300px] lg:h-[340px]">
                <ResponsiveContainer width="100%" height="100%">
                  {renderChart(reportKey, chartData)}
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <EmptyState
              icon={<CalendarX className="h-7 w-7" />}
              title="No data for this period"
              description="Try adjusting the date range or filters."
            />
          )}

          {/* ── Tier 4: Data Table ── */}
          {rawTableData.length > 0 && (
            <div className="glass-card overflow-hidden">
              {/* Table Search */}
              <div className="flex items-center gap-3 border-b border-border/50 px-4 py-3">
                <div className="relative flex-1 max-w-xs">
                  <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search records..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full rounded-md border border-border bg-background py-1.5 pl-8 pr-3 text-xs text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    aria-label="Search report records"
                  />
                </div>
                <span className="text-[11px] text-muted-foreground">
                  {filteredData.length} record{filteredData.length !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Scrollable Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border/50 bg-muted/20">
                      {columnKeys.map((key) => (
                        <th
                          key={key}
                          className="whitespace-nowrap px-3 py-2.5 text-left font-semibold text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                          onClick={() => onSort(key)}
                        >
                          <span className="inline-flex items-center gap-1">
                            {colLabel(key)}
                            {sortKey === key && <ArrowUpDown className="h-3 w-3 text-primary" />}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pageData.map((row, i) => (
                      <tr
                        key={i}
                        className="border-b border-border/30 transition-colors hover:bg-muted/10"
                      >
                        {columnKeys.map((key) => (
                          <td
                            key={key}
                            className="whitespace-nowrap px-3 py-2 text-foreground"
                            title={String(row[key] ?? '')}
                          >
                            {formatCell(key, row[key])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-border/50 px-4 py-2.5">
                  <span className="text-[11px] text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-accent disabled:opacity-40"
                      aria-label="Previous page"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-accent disabled:opacity-40"
                      aria-label="Next page"
                    >
                      <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Chart Renderer ───────────────────────────────────────────

function renderChart(reportKey: string, data: Record<string, unknown>[]): React.ReactElement {
  const gridStroke = 'hsl(var(--border) / 0.4)';
  const axisColor = 'hsl(var(--muted-foreground))';
  const tooltipBg = 'hsl(var(--card))';
  const tooltipBorder = 'hsl(var(--border))';

  const commonAxisProps = {
    tick: { fill: axisColor, fontSize: 11 },
    axisLine: { stroke: gridStroke },
    tickLine: { stroke: gridStroke },
  };

  const tooltipStyle = {
    contentStyle: {
      backgroundColor: tooltipBg,
      border: `1px solid ${tooltipBorder}`,
      borderRadius: '8px',
      fontSize: '11px',
      padding: '8px 12px',
    },
    labelStyle: { fontWeight: 600, marginBottom: '4px' },
  };

  switch (reportKey) {
    case 'sales/summary':
      return (
        <AreaChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
          <XAxis dataKey="label" {...commonAxisProps} />
          <YAxis {...commonAxisProps} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
          <Tooltip
            {...tooltipStyle}
            formatter={(v: number) => [`₹${v.toLocaleString('en-IN')}`, '']}
          />
          <Legend wrapperStyle={{ fontSize: '11px' }} />
          <Area
            type="monotone"
            dataKey="revenue"
            name="Invoice value"
            stroke={CHART_COLORS[0]}
            fill={CHART_COLORS[0]}
            fillOpacity={0.15}
            strokeWidth={2}
          />
          {data.some((row) => row.cogs !== undefined) && (
            <Area
              type="monotone"
              dataKey="cogs"
              name="COGS"
              stroke={CHART_COLORS[3]}
              fill={CHART_COLORS[3]}
              fillOpacity={0.08}
              strokeWidth={2}
            />
          )}
          {data.some((row) => row.profit !== undefined) && (
            <Area
              type="monotone"
              dataKey="profit"
              name="Profit"
              stroke={CHART_COLORS[1]}
              fill={CHART_COLORS[1]}
              fillOpacity={0.15}
              strokeWidth={2}
            />
          )}
        </AreaChart>
      );

    case 'sales/by-product':
      return (
        <BarChart data={data} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
          <XAxis
            type="number"
            {...commonAxisProps}
            tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`}
          />
          <YAxis type="category" dataKey="name" {...commonAxisProps} width={120} />
          <Tooltip
            {...tooltipStyle}
            formatter={(v: number) => [`₹${v.toLocaleString('en-IN')}`, '']}
          />
          <Legend wrapperStyle={{ fontSize: '11px' }} />
          <Bar dataKey="revenue" name="Revenue" fill={CHART_COLORS[0]} radius={[0, 4, 4, 0]} />
          <Bar dataKey="cost" name="Cost" fill={CHART_COLORS[3]} radius={[0, 4, 4, 0]} />
        </BarChart>
      );

    case 'inventory/valuation':
      return (
        <PieChart>
          <Pie
            data={data}
            dataKey="costValue"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius="45%"
            outerRadius="75%"
            paddingAngle={3}
            label={({ name, percent }: { name: string; percent: number }) =>
              `${name} ${(percent * 100).toFixed(0)}%`
            }
            labelLine={true}
          >
            {data.map((_, i) => (
              <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            {...tooltipStyle}
            formatter={(v: number) => [`₹${v.toLocaleString('en-IN')}`, '']}
          />
          <Legend wrapperStyle={{ fontSize: '11px' }} />
        </PieChart>
      );

    case 'financial/receivables-aging':
      return (
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
          <XAxis dataKey="bucket" {...commonAxisProps} />
          <YAxis {...commonAxisProps} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
          <Tooltip
            {...tooltipStyle}
            formatter={(v: number) => [`₹${v.toLocaleString('en-IN')}`, '']}
          />
          <Bar dataKey="amount" name="Outstanding" radius={[4, 4, 0, 0]}>
            {data.map((_, i) => (
              <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
            ))}
          </Bar>
        </BarChart>
      );

    case 'purchases/summary':
      return (
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
          <XAxis dataKey="label" {...commonAxisProps} />
          <YAxis {...commonAxisProps} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
          <Tooltip
            {...tooltipStyle}
            formatter={(v: number) => [`₹${v.toLocaleString('en-IN')}`, '']}
          />
          <Legend wrapperStyle={{ fontSize: '11px' }} />
          <Bar
            dataKey="purchaseAmount"
            name="Purchase Total"
            fill={CHART_COLORS[2]}
            radius={[4, 4, 0, 0]}
          />
          <Bar dataKey="paidAmount" name="Paid" fill={CHART_COLORS[1]} radius={[4, 4, 0, 0]} />
          <Bar
            dataKey="balanceDue"
            name="Balance Due"
            fill={CHART_COLORS[4]}
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      );

    case 'returns/summary':
      return (
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
          <XAxis dataKey="status" {...commonAxisProps} />
          <YAxis {...commonAxisProps} />
          <Tooltip {...tooltipStyle} />
          <Legend wrapperStyle={{ fontSize: '11px' }} />
          <Bar dataKey="count" name="Count" fill={CHART_COLORS[4]} radius={[4, 4, 0, 0]} />
          <Bar
            dataKey="value"
            name="Original invoice value (₹)"
            fill={CHART_COLORS[3]}
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      );

    default:
      return (
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
          <XAxis {...commonAxisProps} />
          <YAxis {...commonAxisProps} />
          <Bar dataKey="value" fill={CHART_COLORS[0]} />
        </BarChart>
      );
  }
}
